import { formatMoney } from "@helmr/ui";
import { useEffect, useRef, useState } from "react";
import { newIdempotencyKey } from "../../api/client";
import { useAppContext, type Context } from "../../app/permissions";

/** Money formatter bound to the business currency and locale (`GET /me`); INR/en-IN only until it loads (default, not logic). */
export function useMoney(ctx: Context) {
  const currency = ctx.me?.business.currency ?? "INR";
  const locale = ctx.locale;
  // A figure the server withheld (a D57 field lever is off) is null and shows as a dash, never as zero.
  return { currency, locale, fmt: (minor: number | null) => (minor === null ? "—" : formatMoney(minor, currency, locale)) };
}

/** The role's discount cap in basis points; owners have none. The cap applies only with `billing.discount`: without it the cap is 0 (D57). */
export function discountCapBp(ctx: Context): number {
  const me = ctx.me;
  if (!me) return 0;
  return ctx.has("billing.discount") ? me.discountCapBp : 0;
}

export function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * One Idempotency-Key per user intent. `current()` returns the same key until `renew()` (after a success).
 * `forBody` mints a fresh key when the body changed since a definitive failure, so a fixed-up retry is never
 * rejected as a mismatch; it keeps the key while the last attempt is still undecided (`pending`).
 */
export function useIdempotentKey() {
  const key = useRef(newIdempotencyKey());
  const body = useRef<string | null>(null);
  const pending = useRef(false);
  return {
    forBody(serialised: string) {
      if (body.current !== null && body.current !== serialised && !pending.current) key.current = newIdempotencyKey();
      body.current = serialised;
      return key.current;
    },
    setPending(v: boolean) { pending.current = v; },
    renew() { key.current = newIdempotencyKey(); body.current = null; pending.current = false; },
  };
}

export { useAppContext };

import { useInfiniteQuery } from "@tanstack/react-query";
import { useServices } from "../../app/services";
import { useSessionSnapshot } from "../../app/permissions";

/** Cache keys carry the business and branch so a switch never shows another tenant's rows. */
export function useScope() {
  const { client, now } = useServices();
  const s = useSessionSnapshot();
  return { client, now, scopeKey: [s.businessId, s.branchId] as const, branchId: s.branchId };
}

export interface PageResult<T> { rows: T[]; next: string | null }

export function usePaged<T>(key: readonly unknown[], fetchPage: (cursor: string | undefined) => Promise<PageResult<T>>, enabled = true) {
  const q = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p: PageResult<T>) => p.next ?? undefined,
    enabled,
  });
  return { ...q, rows: q.data?.pages.flatMap((p) => p.rows) ?? [] };
}
